/* eslint-disable max-lines */
import {
    useState, useEffect, useContext
} from 'react';
import {
    ApiList
} from './ApiList';
import {
    ApiForm
} from './ApiForm';
import {
    ApiResponse
} from './ApiResponse';
import {
    Button
} from 'shared/components/ui/button';
import {
    Plus
} from 'lucide-react';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import Breadcrumb from 'shared/components/Breadcrumb';
import {
    useParams
} from 'react-router-dom';
import {
    Card, CardContent, CardHeader
} from 'shared/components/ui/card';
import {
    apiManagerService,
    projectOService
} from 'shared/api';
import {
    Context
} from 'shared/context/GlobalState';
import {
    AppLoader
} from 'shared/components/LoaderSpinner';
import TestApiDialog from './TestApiDialog';
import {
    PanelErrorBoundary
} from 'shared/components/ErrorBoundary';

function isValidUrl(str) {
    try {
        new URL(str);
        return true;
    } catch (e) {
        return false;
    }
}

export default function ApiManager() {
    const [apis, setApis] = useState([]);
    const [selectedApi, setSelectedApi] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [response, setResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [openTestDialog, setOpenTestDialog] = useState(false);
    const [activeTab, setActiveTab] = useState('request');

    const {
        oragID, subOragID
    } = useParams();
    const {
        addNewNotifcation
    } = useContext(Context);

    useEffect(() => {
        setLoading(true);
        apiManagerService.listApis(oragID, subOragID)
            .then((res) => {
                setApis(
                    res.data.map(api => {
                    // Normalize headers
                        let rawHeaders = api.headers ?? api.headersDef ?? {};

                        // Step 2: Normalize to object if it's an array
                        let normalizedHeaders = rawHeaders;
                        if (Array.isArray(rawHeaders)) {
                            normalizedHeaders = Object.fromEntries(
                                rawHeaders.map(header => [header.key, header.value])
                            );
                        }

                        const normalizedApi = {
                            ...api,
                            headers: normalizedHeaders,
                            auth: api.auth ?? api.authDef ?? {
                                type: 'none',
                                token: '',
                                username: '',
                                password: ''
                            },
                            input_schema: JSON.stringify(api.input_schema, null, 2),
                            output_schema: JSON.stringify(api.output_schema, null, 2)
                        };
                        delete normalizedApi['headersDef'];
                        delete normalizedApi['authDef'];
                        return normalizedApi;
                    })
                );
            }).catch((err) => {
                console.error('Error fetching APIs:', err);
            }).finally(() => {
                setLoading(false);
            });
    }, []);

    const handleAddNew = () => {
        setSelectedApi({
            id: Date.now().toString(),
            name: '',
            paramsDef: [],
            url: '',
            method: 'GET',
            headers: {},
            input_schema: '',
            output_schema: '',
            timeout: 10000,
            auth: {
                type: 'none',
                token: '',
                username: '',
                password: ''
            },
        });
        setIsEditing(true);
        setResponse(null);
        setActiveTab('request');
        setOpen(true);
    };

    const handleEdit = (api) => {
        setSelectedApi(api);
        setIsEditing(true);
        setResponse(null);
        setActiveTab('request');
        setOpen(true);
    };

    const handleSelect = (api) => {
        setSelectedApi(api);
        setIsEditing(false);
        setResponse(null);
        setActiveTab('request');
    };

    const handleDelete = async (id) => {
        const deleteObj = {
            organizationId: oragID,
            subOrganizationId: subOragID,
            apiId: id,
        };
        await apiManagerService.deleteApi(deleteObj);
        setApis(apis.filter((api) => api._id !== id));
        addNewNotifcation('Api deleted successfully', 'success');

        if (selectedApi?._id === id) {
            setSelectedApi(null);
            setIsEditing(false);
            setResponse(null);
        }
    };

    const handleSave = async (api) => {
        const apiObj = {
            organizationId: oragID,
            subOrganizationId: subOragID,
            name: api.name,
            url: api.url,
            timeout: api.timeout,
            method: api.method,
            headers: api.headers,
            paramsDef: api.paramsDef,
            auth: api.auth || { type: 'none' },
        };

        if (api.input_schema) {
            apiObj['input_schema'] = api.input_schema;
        }

        if (api.output_schema) {
            apiObj['output_schema'] = api.output_schema;
        }

        // check url data
        if (!isValidUrl(apiObj.url)) {
            return addNewNotifcation('Please enter valid url');
        }

        const isUpdate = apis.some((a) => a._id === api._id);
        if (isUpdate) {
            apiObj.apiId = api._id;
        }

        try {
            const res = await apiManagerService.persistApiWithVaultAuth(
                apiObj,
                api._vaultSecrets || {},
                api._headerVaultSecrets || {},
                api._keepSecretHeaderNames || [],
            );
            const savedApi = {
                ...res.data,
                input_schema: JSON.stringify(res.data.input_schema, null, 2),
                output_schema: JSON.stringify(res.data.output_schema, null, 2),
            };
            if (isUpdate) {
                setApis(apis.map((a) => (a._id === api._id ? savedApi : a)));
                addNewNotifcation('API updated successfully', 'success');
            } else {
                setApis([...apis, savedApi]);
                addNewNotifcation('API created successfully', 'success');
            }
            setSelectedApi(savedApi);
        } catch (err) {
            addNewNotifcation('Something went wrong', 'danger');
            console.error('Error saving API:', err);
        }
        setIsEditing(false);
        setOpen(false);
    };

    const handleCancel = () => {
        // If canceling a new API (not in the saved list), clear the selection
        if (selectedApi && !apis.some((a) => a._id === selectedApi._id)) {
            setSelectedApi(null);
            setResponse(null);
        }
        setIsEditing(false);
        setOpen(false);
    };

    const handleTest = async (testData) => {
        setIsLoading(true);
        setActiveTab('response');

        try {
            // Prepare headers from test data
            const headers = {
                ...testData.headers
            };

            // Convert params array to object
            const params = testData.paramsDef?.reduce((acc, {
                key, value
            }) => {
                if (key) acc[key] = value;
                return acc;
            }, {}) || {};

            applyAuthorizationHeader(headers, testData.auth);

            // Make the request
            const startTime = performance.now();

            const data = {
                url: selectedApi.url,
                method: selectedApi.method,
                headers: headers,
                timeout: selectedApi.timeout,
                query_params: params
            };

            // Add body if provided and not GET request
            if (selectedApi.method !== 'GET' && testData.body) {
                try {
                    data.body = JSON.parse(testData.body);
                } catch (e) {
                    console.error('Error parsing body:', e);
                }
            }

            // Add output schema for validation if available
            if (selectedApi.output_schema) {
                try {
                    data.output_schema = JSON.parse(selectedApi.output_schema);
                } catch (e) {
                    console.error('Error setting output schema:', e);
                }
            }

            const endTime = performance.now();
            const responseTime = endTime - startTime;

            // Try to parse response as JSON
            try {
                const res = await projectOService.performRequestWithFullResponse('projecto/config/api/v1/api_manager/call_and_validate', {
                    data: data,
                    method: 'POST',
                });

                setResponse({
                    status: res.status,
                    statusText: res.statusText,
                    headers: Object.fromEntries([...res.headers]),
                    body: res.data,
                    responseTime: responseTime.toFixed(2),
                });
            } catch (error) {
                setResponse({
                    status: error.status,
                    statusText: error.statusText,
                    body: error,
                    responseTime: responseTime.toFixed(2)
                });
            }
        } catch (error) {
            setResponse({
                error: true,
                message: error instanceof Error ? error.message : 'An unknown error occurred',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='flex flex-col overflow-y-scroll w-full p-8'>
            <div className='w-full flex flex-col gap-4'>
                <div className='my-3 px-2'>
                    <Breadcrumb maxPaths={2} />
                </div>

                <Card>
                    <CardHeader>
                        <h2 className="text-2xl font-bold">API Manager</h2>
                    </CardHeader>
                    <CardContent className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                        <div className="md:col-span-1 space-y-4">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold">Saved APIs</h2>
                                <Button onClick={handleAddNew} size="sm">
                                    <Plus className="h-4 w-4 mr-2" />
                                    New API
                                </Button>
                            </div>
                            {loading ? (
                                <div className="flex items-center justify-center h-full">
                                    <AppLoader size={30} />
                                </div>
                            ) : (
                                <PanelErrorBoundary
                                    name="api-manager-list"
                                    title="The saved API list could not be displayed"
                                    recordType="api"
                                    resetKeys={[apis]}
                                >
                                    <ApiList
                                        apis={apis}
                                        onSelect={handleSelect}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                        selectedId={selectedApi?._id}
                                    />
                                </PanelErrorBoundary>
                            )}
                        </div>

                        <div className="md:col-span-2">
                            {/* Tier 3 — the record-shaped panel. This column renders a
                                stored API document (paramsDef, headers, schemas), and
                                `redisApiSchema` has no item schema for paramsDef, so three
                                different shapes for one field are all storable. One legacy
                                row in the wrong shape used to blank the whole platform;
                                now it costs this column and the saved-API list beside it
                                stays usable, so the user can select or delete the bad one. */}
                            <PanelErrorBoundary
                                name="api-manager-detail"
                                title="This API record could not be displayed"
                                recordType="api"
                                recordId={selectedApi?._id}
                                recordLabel={selectedApi?.name}
                            >
                                {selectedApi ? (
                                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                                        <TabsList className="mb-4">
                                            <TabsTrigger value="request">Request</TabsTrigger>
                                            <TabsTrigger value="response">Response</TabsTrigger>
                                        </TabsList>

                                        <TabsContent value="request">
                                            {isEditing && (
                                                <PanelErrorBoundary
                                                    name="api-manager-form"
                                                    title="This API's editor could not be displayed"
                                                    recordType="api"
                                                    recordId={selectedApi?._id}
                                                    recordLabel={selectedApi?.name}
                                                >
                                                    <ApiForm api={selectedApi} onSave={handleSave} onCancel={handleCancel} open={open} setOpen={setOpen} />
                                                </PanelErrorBoundary>
                                            )}
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center">
                                                    <h2 className="text-xl font-semibold">{selectedApi.name} ({selectedApi._id} )</h2>
                                                    <div className="space-x-2">
                                                        <Button onClick={() => handleEdit(selectedApi)} variant="outline" size="sm">
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            onClick={() => setOpenTestDialog(true)}
                                                            size="sm" disabled={isLoading}>
                                                            {isLoading ? 'Testing...' : 'Test API'}
                                                        </Button>

                                                        {/* Test API Dialog */}
                                                        <TestApiDialog
                                                            open={openTestDialog}
                                                            setOpen={setOpenTestDialog}
                                                            api={selectedApi}
                                                            onTest={handleTest}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="border rounded-md p-4 space-y-4">
                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-bold rounded">
                                                            {selectedApi.method}
                                                        </span>
                                                        <span className="text-sm font-mono truncate">{selectedApi.url}</span>
                                                    </div>

                                                    {Object.keys(selectedApi.headers).length > 0 && (
                                                        <div>
                                                            <h3 className="text-sm font-semibold mb-2">Headers</h3>
                                                            <div className="bg-muted p-2 rounded-md">
                                                                {Object.entries(selectedApi.headers).map(([key, value], index) => (
                                                                    <div key={index} className="text-xs font-mono truncate">
                                                                        {key}: {value}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {selectedApi.auth && selectedApi.auth?.type !== 'none' && (
                                                        <div>
                                                            <h3 className="text-sm font-semibold mb-2">Authorization</h3>
                                                            <div className="bg-muted p-2 rounded-md">
                                                                <div className="text-xs font-mono">
                                                                    Type: {selectedApi.auth?.type}
                                                                    {selectedApi.auth?.type === 'bearer' &&
                                                                        ` (Token: ${selectedApi.auth?.token?.substring(0, 5)}...)`}
                                                                    {selectedApi.auth?.type === 'basic' && ` (Username: ${selectedApi.auth?.username})`}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {selectedApi.input_schema && (
                                                        <div>
                                                            <h3 className="text-sm font-semibold mb-2">Body</h3>
                                                            <div className="bg-muted p-2 rounded-md">
                                                                <pre className="text-xs font-mono whitespace-pre-wrap">{selectedApi.input_schema}</pre>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </TabsContent>

                                        <TabsContent value="response">
                                            <PanelErrorBoundary
                                                name="api-manager-response"
                                                title="The response could not be rendered"
                                                recordType="api"
                                                recordId={selectedApi?._id}
                                                recordLabel={selectedApi?.name}
                                                resetKeys={[response]}
                                            >
                                                <ApiResponse response={response} isLoading={isLoading} />
                                            </PanelErrorBoundary>
                                        </TabsContent>
                                    </Tabs>
                                ) : (
                                    <div className="border border-dashed rounded-md p-8 text-center">
                                        <p className="text-muted-foreground mb-4">No API selected</p>
                                        <Button onClick={handleAddNew} variant="outline">
                                            <Plus className="h-4 w-4 mr-2" />
                                            Create New API
                                        </Button>
                                    </div>
                                )}
                            </PanelErrorBoundary>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function encodeBasicAuth(username, password) {
    const creds = `${username ?? ''}:${password ?? ''}`;
    if (typeof TextEncoder !== 'undefined') {
        const bytes = new TextEncoder().encode(creds);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        return `Basic ${btoa(binary)}`;
    }
    return `Basic ${btoa(creds)}`;
}

function applyAuthorizationHeader(headers, apiAuth) {
    if (!apiAuth?.type || apiAuth.type === 'none') return;
    // Vault-backed docs no longer expose plaintext secrets to the browser.
    // Live test from this legacy panel needs a one-shot token/password in the form,
    // or use DE Studio ApiTestPanel which resolves refs server-side.
    const type = String(apiAuth.type).toLowerCase();
    if (type === 'bearer') {
        const token = (apiAuth.token || '').trim();
        if (token) headers.Authorization = `Bearer ${token}`;
    } else if (type === 'basic') {
        const username = apiAuth.username ?? '';
        const password = apiAuth.password ?? '';
        if (String(username).trim() || String(password)) {
            headers.Authorization = encodeBasicAuth(username, password);
        }
    }
}
